import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';

/**
 * Service pour gérer la synchronisation calendrier iCal
 */
export const calendarService = {
  /**
   * Récupère l'URL du flux calendrier iCal pour l'utilisateur connecté
   * @returns L'URL complète du flux iCal
   */
  async getCalendarFeedUrl(): Promise<string> {
    try {
      // Récupérer l'utilisateur connecté
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Récupérer le calendar_token de l'utilisateur
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('calendar_token')
        .eq('id', userData.user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Impossible de récupérer le profil utilisateur');
      }

      if (!profile.calendar_token) {
        throw new Error('Token calendrier non trouvé. Veuillez régénérer le lien.');
      }

      // Construire l'URL du flux iCal
      // Utiliser l'API Express locale au lieu des Edge Functions Supabase
      const apiUrl = process.env.REACT_APP_CALENDAR_API_URL || 'http://localhost:3001';
      
      const feedUrl = `${apiUrl}/api/calendar/feed?token=${profile.calendar_token}`;
      
      logger.info('URL du flux calendrier générée avec succès');
      return feedUrl;

    } catch (error) {
      logger.error('Erreur lors de la récupération de l\'URL du flux calendrier:', error);
      throw error;
    }
  },

  /**
   * Régénère le token calendrier de l'utilisateur (révoque l'ancien accès)
   * @returns Le nouveau token généré
   */
  async regenerateCalendarToken(): Promise<string> {
    try {
      // Récupérer l'utilisateur connecté
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Appeler la fonction SQL pour régénérer le token
      const { data, error } = await supabase.rpc('regenerate_calendar_token', {
        user_id_param: userData.user.id
      });

      if (error) {
        throw error;
      }

      logger.info('Token calendrier régénéré avec succès');
      return data as string;

    } catch (error) {
      logger.error('Erreur lors de la régénération du token calendrier:', error);
      throw error;
    }
  },

  /**
   * Copie l'URL du flux calendrier dans le presse-papier
   * @param url L'URL à copier
   * @returns true si la copie a réussi, false sinon
   */
  async copyToClipboard(url: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        logger.info('URL copiée dans le presse-papier');
        return true;
      } else {
        // Fallback pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          logger.info('URL copiée dans le presse-papier (fallback)');
          return true;
        } else {
          logger.warn('Échec de la copie dans le presse-papier');
          return false;
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la copie dans le presse-papier:', error);
      return false;
    }
  },

  /**
   * Vérifie si le calendrier est accessible (teste l'URL)
   * @param url L'URL du flux calendrier
   * @returns true si accessible, false sinon
   */
  async testCalendarFeed(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      return response.ok;
    } catch (error) {
      logger.error('Erreur lors du test du flux calendrier:', error);
      return false;
    }
  },

  /**
   * Récupère les statistiques du calendrier (nombre d'événements)
   * @returns Le nombre d'événements à venir
   */
  async getCalendarStats(): Promise<{
    totalEvents: number;
    notifications: number;
    actions: number;
  }> {
    try {
      // Récupérer l'utilisateur connecté
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        throw new Error('Utilisateur non authentifié');
      }

      const now = new Date().toISOString();

      // Compter les notifications avec reminder_date à venir
      const { count: notifCount, error: notifError } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userData.user.id)
        .eq('is_active', true)
        .not('reminder_date', 'is', null)
        .gte('reminder_date', now);

      if (notifError) {
        logger.error('Erreur lors du comptage des notifications:', notifError);
      }

      // Compter les actions avec reminder_date ou next_follow_up à venir
      const { count: actionsCount, error: actionsError } = await supabase
        .from('actions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userData.user.id)
        .eq('status', 'pending')
        .or(`reminder_date.gte.${now},next_follow_up.gte.${now}`);

      if (actionsError) {
        logger.error('Erreur lors du comptage des actions:', actionsError);
      }

      const notifications = notifCount || 0;
      const actions = actionsCount || 0;
      const totalEvents = notifications + actions;

      logger.info(`Statistiques calendrier: ${totalEvents} événements (${notifications} notifications, ${actions} actions)`);

      return {
        totalEvents,
        notifications,
        actions
      };

    } catch (error) {
      logger.error('Erreur lors de la récupération des statistiques du calendrier:', error);
      return {
        totalEvents: 0,
        notifications: 0,
        actions: 0
      };
    }
  }
};
